import React, { useEffect, useState } from 'react';
import {
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  makeStyles,
  TextField,
  InputAdornment,
} from '@material-ui/core';
import { Add, KeyboardArrowDown, KeyboardArrowUp, Save } from '@material-ui/icons';
import { wsGetAll } from 'services/WService';
import { wsUpdate } from 'services/WService';
import RegularButton from 'components/CustomButtons/Button';

const useRowStyles = makeStyles({
  root: {
    '& > *': {
      borderBottom: 'unset',
    },
  },
});

function ZoneSellerTable(props) {
  const { quotaZone, open, resetZoneQuota, showNotify } = props;
  const { addZoneSellerModal, setAddZoneSellerModal } = props;
  const [sellerQuotas, setSellerQuotas] = useState([]);
  const [loaded, setLoaded] = useState(false);

  async function loadSellers() {
    let res = await wsGetAll(`quota/zone/${quotaZone.id}/seller`);
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));
      setSellerQuotas(data);
    }
  }

  useEffect(() => {
    if (!open) return;
    if (loaded) return;
    setLoaded(true);
    loadSellers();
  }, [open]);

  return (
    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={100}>
      <Collapse in={open} timeout="auto">
        <Box sx={{ margin: 1, marginLeft: '30px' }}>
          <Typography variant="h6" gutterBottom component="div">
            {loaded && sellerQuotas.length === 0 ? 'Cargando...' : 'Vista de vendedores'}
          </Typography>

          {loaded && sellerQuotas.length > 0 && (
            <TableContainer component={Paper}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell align="left">Vendedor</TableCell>
                    <TableCell>Cuota sugerida</TableCell>
                    <TableCell>Cuota</TableCell>
                    <TableCell>Cuota IGV</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sellerQuotas.map((sellerQuota) => {
                    return (
                      <ZoneSellerItem
                        key={sellerQuota.base_seller_id}
                        sellerQuota={sellerQuota}
                        resetZoneQuota={resetZoneQuota}
                        showNotify={showNotify}
                        setSellerQuotas={setSellerQuotas}
                        setAddZoneSellerModal={setAddZoneSellerModal}
                        quotaZone={quotaZone}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Collapse>
    </TableCell>
  );
}

function ZoneSellerItem(props) {
  const {
    sellerQuota,
    resetZoneQuota,
    showNotify,
    setSellerQuotas,
    setAddZoneSellerModal,
    quotaZone,
  } = props;
  const classes = useRowStyles();
  const [originalZoneSellerQuota, setOriginalZoneSellerQuota] = useState(sellerQuota);
  const [quota, setQuota] = useState(sellerQuota.quota);
  const [quotaIgv, setQuotaIgv] = useState(sellerQuota.quota_igv || 0);
  const [sellerDetails, setSellerDetails] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [open, setOpen] = useState(false);
  const [processing, setProcessing] = useState(false);

  async function loadDetails() {
    let res = await wsGetAll(
      `quota/zone/${sellerQuota.quota_zone_id}/seller/${sellerQuota.base_seller_id}`,
    );
    if (res.success) {
      let data = res.data.map((row) => ({
        ...row,
        quota_suggested: parseFloat(row.quota_suggested).toFixed(2),
        quota: parseFloat(row.quota).toFixed(2),
        quota_igv: parseFloat(row.quota_igv || 0).toFixed(2),
      }));
      setSellerDetails(data);
    }
  }

  async function updateZoneSeller() {
    setProcessing(true);
    let res = await wsUpdate(
      `quota/zone/${sellerQuota.quota_zone_id}/seller/${sellerQuota.base_seller_id}`,
      {
        quota,
        quota_igv: quotaIgv,
      },
    );
    setProcessing(false);
    if (res.success) {
      resetZoneQuota(res.data.zone_quota);
      resetZoneSeller(res.data);
      showNotify(res.message);
    } else {
      showNotify(res.message, 'error');
    }
  }

  function resetZoneSeller(zoneSeller) {
    let quota = parseFloat(zoneSeller.quota).toFixed(2);
    let quotaIgv = parseFloat(zoneSeller.quota_igv).toFixed(2);
    setQuota(quota);
    setQuotaIgv(quotaIgv);
    setOriginalZoneSellerQuota((originalQuota) => ({
      ...originalQuota,
      quota,
      quota_igv: quotaIgv,
    }));
    setSellerQuotas((sellerQuotas) => {
      let newSellerQuotas = [...sellerQuotas];
      let index = newSellerQuotas.findIndex(
        (detail) => parseInt(detail.base_seller_id) === parseInt(zoneSeller.base_seller_id),
      );
      newSellerQuotas[index].quota = quota;
      newSellerQuotas[index].quota_igv = quotaIgv;
      return newSellerQuotas;
    });
  }

  function handleChange(e) {
    setSellerDetails([]);
    setLoaded(false);
    setOpen(false);
    setQuota(e.target.value);
  }

  function handleChangeIgv(e) {
    setSellerDetails([]);
    setLoaded(false);
    setOpen(false);
    setQuotaIgv(e.target.value);
  }

  useEffect(() => {
    if (!open) return;
    if (loaded) return;
    setLoaded(true);
    loadDetails();
  }, [open, loaded]);

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell width={50}>
          {originalZoneSellerQuota.quota !== quota ? (
            <img src="/img/pencil.gif" width="35" />
          ) : (
            <IconButton
              aria-label="expand row"
              size="small"
              onClick={() => setOpen((open) => !open)}
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell align="left">
          {sellerQuota.seller.SlpName} ({sellerQuota.seller.SlpCode})
        </TableCell>
        <TableCell width={200}>
          <TextField value={sellerQuota.quota_suggested} disabled />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quota}
            onChange={handleChange}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={updateZoneSeller}
                    disabled={originalZoneSellerQuota.quota === quota || processing}
                  >
                    <Save
                      style={{
                        color:
                          originalZoneSellerQuota.quota === quota || processing
                            ? undefined
                            : '#4caf50',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
        <TableCell width={200}>
          <TextField
            value={quotaIgv}
            onChange={handleChangeIgv}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={updateZoneSeller}
                    disabled={originalZoneSellerQuota.quota_igv === quotaIgv || processing}
                  >
                    <Save
                      style={{
                        color:
                          originalZoneSellerQuota.quota_igv === quotaIgv || processing
                            ? undefined
                            : '#4caf50',
                      }}
                    />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
          <Collapse in={open} timeout="auto">
            <Box sx={{ margin: 1, marginLeft: '30px' }}>
              <Typography variant="h6" gutterBottom component="div">
                {loaded && sellerDetails.length === 0 ? 'Cargando...' : 'Proveedores / Marcas'}
              </Typography>

              <RegularButton
                justIcon
                color="success"
                onClick={() =>
                  setAddZoneSellerModal({
                    show: true,
                    sellerQuota: sellerQuota,
                    quotaZone: quotaZone,
                    loadDetails: loadDetails,
                  })
                }
              >
                <Add />
              </RegularButton>

              {loaded && sellerDetails.length > 0 && (
                <TableContainer component={Paper}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell align="left">Proveedor</TableCell>
                        <TableCell align="left">Marca</TableCell>
                        <TableCell>Cuota sugerida</TableCell>
                        <TableCell>Cuota</TableCell>
                        <TableCell>Cuota IGV</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sellerDetails.map((sellerDetail) => {
                        return (
                          <SellerDetail
                            key={`${sellerDetail.base_business_partner_id}-${sellerDetail.logi_mark_id}`}
                            sellerDetail={sellerDetail}
                            showNotify={showNotify}
                            resetZoneQuota={resetZoneQuota}
                            resetZoneSeller={resetZoneSeller}
                            setSellerDetails={setSellerDetails}
                          />
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function SellerDetail(props) {
  const { sellerDetail, showNotify, resetZoneQuota, resetZoneSeller, setSellerDetails } = props;
  const [processing, setProcessing] = useState(false);
  const [originalSellerDetail, setOriginalSellerDetail] = useState(sellerDetail);
  const [quota, setQuota] = useState(sellerDetail.quota);
  const [quotaIgv, setQuotaIgv] = useState(sellerDetail.quota_igv);

  async function updateDetail() {
    setProcessing(true);
    let res = await wsUpdate(
      `quota/zone/${sellerDetail.quota_zone_id}/seller/${sellerDetail.base_seller_id}/provider`,
      {
        base_business_partner_id: sellerDetail.base_business_partner_id,
        logi_mark_id: sellerDetail.logi_mark_id,
        quota,
        quota_igv: quotaIgv,
      },
    );
    setProcessing(false);

    if (res.success) {
      resetZoneQuota(res.data.zone_quota);
      resetZoneSeller(res.data.zone_seller);

      let quota = parseFloat(res.data.quota).toFixed(2);
      let quotaIgv = parseFloat(res.data.quota_igv || 0).toFixed(2);
      setQuota(quota);
      setQuotaIgv(quotaIgv);
      setOriginalSellerDetail((originalDetail) => ({
        ...originalDetail,
        quota,
        quota_igv: quotaIgv,
      }));
      setSellerDetails((details) => {
        let newDetails = [...details];
        let index = newDetails.findIndex(
          (detail) =>
            detail.base_business_partner_id === res.data.base_business_partner_id &&
            detail.logi_mark_id === res.data.logi_mark_id,
        );
        newDetails[index].quota = quota;
        newDetails[index].quota_igv = quotaIgv;
        return newDetails;
      });
      showNotify(res.message);
    } else {
      showNotify(res.message, 'error');
    }
  }

  return (
    <TableRow>
      <TableCell width={50}>
        {originalSellerDetail.quota !== quota && <img src="/img/pencil.gif" width="35" />}
      </TableCell>
      {/* Proveedor */}
      <TableCell align="left">
        {sellerDetail.provider.CardName} ({sellerDetail.provider.CardCode})
      </TableCell>
      {/* Marca */}
      <TableCell align="left">
        {sellerDetail.mark.description} ({sellerDetail.mark.code})
      </TableCell>
      <TableCell width={200}>
        <TextField value={sellerDetail.quota_suggested} disabled />
      </TableCell>
      <TableCell width={200}>
        <TextField
          value={quota}
          onInput={(e) => setQuota(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={updateDetail}
                  disabled={originalSellerDetail.quota === quota || processing}
                >
                  <Save
                    style={{
                      color:
                        originalSellerDetail.quota === quota || processing ? undefined : '#4caf50',
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
      <TableCell width={200}>
        <TextField
          value={quotaIgv}
          onInput={(e) => setQuotaIgv(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={updateDetail}
                  disabled={originalSellerDetail.quota_igv === quotaIgv || processing}
                >
                  <Save
                    style={{
                      color:
                      originalSellerDetail.quota_igv === quotaIgv || processing
                          ? undefined
                          : '#4caf50',
                    }}
                  />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </TableCell>
    </TableRow>
  );
}

export default ZoneSellerTable;
